import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import {
  Upload,
  FileText,
  X,
  ChevronRight,
  AlertTriangle,
  Clock,
  Shield,
  Music,
  Briefcase,
  Home,
  Users,
  FileSignature,
  Lock,
  HelpCircle,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CONTRACT_TYPES = [
  { value: '', label: 'Select type...' },
  { value: 'music_recording', label: 'Music Recording Agreement' },
  { value: 'distribution', label: 'Distribution Deal' },
  { value: 'publishing', label: 'Publishing Agreement' },
  { value: 'management', label: 'Management Contract' },
  { value: 'licensing', label: 'Licensing Deal' },
  { value: 'entertainment', label: 'Entertainment Contract' },
  { value: 'employment', label: 'Employment Agreement' },
  { value: 'service', label: 'Service Agreement' },
  { value: 'lease', label: 'Lease Agreement' },
  { value: 'nda', label: 'Non-Disclosure Agreement' },
  { value: 'business', label: 'Business Contract' },
  { value: 'endorsement', label: 'Brand Endorsement' },
  { value: 'other', label: 'Other' },
];

const USER_ROLES = [
  { value: '', label: 'Select your role...' },
  { value: 'artist', label: 'Artist' },
  { value: 'producer', label: 'Producer' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'employee', label: 'Employee' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'licensee', label: 'Licensee' },
  { value: 'other', label: 'Other' },
];

const INDUSTRIES = [
  { value: '', label: 'Select industry...' },
  { value: 'music', label: 'Music & Entertainment' },
  { value: 'technology', label: 'Technology' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance' },
  { value: 'retail', label: 'Retail' },
  { value: 'education', label: 'Education' },
  { value: 'legal', label: 'Legal' },
  { value: 'other', label: 'Other' },
];

const ContractPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Metadata fields
  const [title, setTitle] = useState('');
  const [contractType, setContractType] = useState('');
  const [industry, setIndustry] = useState('');
  const [dateSigned, setDateSigned] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userContext, setUserContext] = useState('');

  // History
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const isPaidUser = user?.subscription_plan && 
    !['free', 'pro_se_starter'].includes(user.subscription_plan.toLowerCase());

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  }, []);

  const validateAndSetFile = (f) => {
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'docx', 'txt'].includes(ext)) {
      toast({ title: 'Unsupported file type', description: 'Please upload a PDF, DOCX, or TXT file.', variant: 'destructive' });
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max file size is 15MB.', variant: 'destructive' });
      return;
    }
    setFile(f);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const removeFile = () => setFile(null);

  const handleUploadAndAnalyze = async () => {
    if (!file) return;

    const token = localStorage.getItem('token');
    if (!token) {
      toast({ title: 'Please log in', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      formData.append('contract_type', contractType);
      formData.append('industry', industry);
      formData.append('date_signed', dateSigned);
      formData.append('user_role', userRole);
      formData.append('user_context', userContext);

      const uploadRes = await axios.post(`${API_BASE}/api/contract/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const contractId = uploadRes.data.contract_id;
      toast({ title: 'Contract uploaded', description: 'Starting AI analysis...' });

      setUploading(false);
      setAnalyzing(true);

      const analyzeRes = await axios.post(
        `${API_BASE}/api/contract/analyze`,
        { contract_id: contractId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (analyzeRes.data.analysis) {
        toast({ title: 'Analysis complete', description: 'Redirecting to results...' });
        navigate(`/dashboard/contract-analysis/${contractId}`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload or analysis failed. Please try again.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const loadHistory = async () => {
    setShowHistory(true);
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/contract/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.contracts || []);
    } catch {
      toast({ title: 'Failed to load history', variant: 'destructive' });
    } finally {
      setLoadingHistory(false);
    }
  };

  if (!isPaidUser) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Upgrade Required</h2>
          <p className="text-gray-600 mb-6">
            The AI Contract Portal is available for paid subscribers. Upgrade your plan to access powerful AI-driven contract analysis.
          </p>
          <Button onClick={() => navigate('/dashboard/subscription')} className="bg-blue-600 hover:bg-blue-700">
            View Plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="contract-portal">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
          AI Contract Portal
        </h1>
        <p className="text-gray-500 text-sm sm:text-base">
          Upload a contract for AI-powered analysis. Get a structured breakdown, risk flags, and actionable next steps.
        </p>
      </div>

      {/* Detailed Description */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4" data-testid="contract-portal-description">
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-2">What is the AI Contract Portal?</h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            The <strong>AI Contract Portal</strong> is your personal contract analysis tool powered by artificial intelligence. Upload any contract, agreement, lease, loan document, terms of service, or legal notice — and the AI will perform a <strong>comprehensive clause-by-clause review</strong>. It identifies hidden risks, one-sided provisions, ambiguous language, and terms that may compromise your rights. Every analysis is grounded in principles of Equity Jurisprudence and Common Law, highlighting where the document may violate fairness, good faith, or due process.
          </p>
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-2">What It Analyzes</h3>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li><strong>Key Terms & Obligations</strong> — Identifies what you are agreeing to and what is expected of each party</li>
            <li><strong>Risk Flags</strong> — Highlights clauses that could expose you to liability, penalties, or loss of rights</li>
            <li><strong>One-Sided Provisions</strong> — Detects unfair terms that disproportionately favor the other party</li>
            <li><strong>Missing Protections</strong> — Points out standard protections or clauses that are absent from the contract</li>
            <li><strong>Actionable Recommendations</strong> — Provides specific next steps, suggested modifications, and points to negotiate</li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-2">How to Use It</h3>
          <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
            <li><strong>Upload your contract</strong> — Drag and drop or click to select a PDF, DOCX, or TXT file</li>
            <li><strong>Add context</strong> (optional) — Describe your concerns or specific clauses you want the AI to focus on</li>
            <li><strong>Click "Analyze Contract"</strong> — The AI will process the document and generate a detailed report</li>
            <li><strong>Review the results</strong> — Examine the risk flags, clause breakdown, and recommendations</li>
            <li><strong>Take action</strong> — Use the insights to negotiate better terms, seek clarification, or identify deal-breakers before signing</li>
          </ol>
        </div>
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            This is an AI-powered legal education tool. It does not create an attorney-client relationship. 
            Complex matters should be reviewed by a qualified professional.
          </span>
        </div>
      </div>

      {/* Upload Area */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 space-y-5">
          {/* Drop Zone */}
          <div
            data-testid="contract-upload-dropzone"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
              ${dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : file 
                  ? 'border-emerald-400 bg-emerald-50' 
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'}
            `}
            onClick={() => !file && document.getElementById('contract-file-input').click()}
          >
            <input
              id="contract-file-input"
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="contract-file-input"
            />
            
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-emerald-600" />
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <button 
                  onClick={(e) => { e.stopPropagation(); removeFile(); }} 
                  className="ml-4 p-1 hover:bg-red-100 rounded-full transition"
                  data-testid="remove-file-btn"
                >
                  <X className="w-5 h-5 text-red-500" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="font-medium text-gray-700">
                  Drag & drop your contract here, or <span className="text-blue-600 underline">browse files</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">Supports PDF, DOCX, TXT (max 15MB)</p>
              </>
            )}
          </div>

          {/* Metadata Fields */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Contract Details <span className="text-gray-400 font-normal">(optional)</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Contract Title</label>
                <Input
                  data-testid="contract-title-input"
                  placeholder="e.g., Artist Recording Agreement"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Contract Type</label>
                <select
                  data-testid="contract-type-select"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={contractType}
                  onChange={(e) => setContractType(e.target.value)}
                >
                  {CONTRACT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Industry</label>
                <select
                  data-testid="contract-industry-select"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  {INDUSTRIES.map((i) => (
                    <option key={i.value} value={i.value}>{i.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date Signed / Received</label>
                <Input
                  data-testid="contract-date-input"
                  type="date"
                  value={dateSigned}
                  onChange={(e) => setDateSigned(e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Your Role in this Contract</label>
                <select
                  data-testid="contract-role-select"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                >
                  {USER_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* User Context / Concerns */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Add Context <span className="text-gray-400 font-normal">(optional)</span>
            </h3>
            <p className="text-xs text-gray-500">Describe any specific concerns, clauses you want the AI to focus on, or questions you have about this contract.</p>
            <Textarea
              data-testid="contract-user-context"
              placeholder="e.g., I'm concerned about the termination clause in Section 5 — it seems to allow the other party to cancel without notice. Also, is the non-compete reasonable? I want to know if the arbitration clause limits my right to sue..."
              value={userContext}
              onChange={(e) => setUserContext(e.target.value)}
              className="min-h-[100px] text-sm"
            />
          </div>

          {/* Analyze Button */}
          <Button
            data-testid="analyze-contract-btn"
            onClick={handleUploadAndAnalyze}
            disabled={!file || uploading || analyzing}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-base font-semibold disabled:opacity-50"
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Uploading...
              </span>
            ) : analyzing ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                AI is analyzing your contract...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Analyze Contract
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* History Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <button
          data-testid="view-history-btn"
          onClick={loadHistory}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition rounded-xl"
        >
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Previous Analyses</span>
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
        </button>

        {showHistory && (
          <div className="border-t border-gray-100 p-4">
            {loadingHistory ? (
              <div className="flex justify-center py-6">
                <span className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No previous analyses yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((c) => (
                  <button
                    key={c.contract_id}
                    data-testid={`history-item-${c.contract_id}`}
                    onClick={() => navigate(`/dashboard/contract-analysis/${c.contract_id}`)}
                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 border border-gray-100 transition text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {c.metadata?.title || c.original_filename}
                        </p>
                        <p className="text-xs text-gray-500">
                          {c.metadata?.contract_type && <span className="capitalize">{c.metadata.contract_type.replace(/_/g, ' ')}</span>}
                          {c.metadata?.contract_type && ' · '}
                          {new Date(c.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${
                      c.status === 'analyzed' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {c.status === 'analyzed' ? 'Analyzed' : 'Pending'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileSignature className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm">Clause-by-Clause</h3>
          <p className="text-xs text-gray-500">Every major clause broken down in plain English with risk ratings.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
          <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm">Risk Detection</h3>
          <p className="text-xs text-gray-500">Color-coded alerts for hidden penalties, one-sided terms, and traps.</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
          <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-900 text-sm">Equity Review</h3>
          <p className="text-xs text-gray-500">Fairness analysis covering power balance, disclosure, and mutuality.</p>
        </div>
      </div>
    </div>
  );
};

export default ContractPortal;
